
import { supabase } from '@/lib/supabase';
import { safeLog } from '../utils/safeLog';

export interface UserConsent {
  id: string;
  user_id: string;
  consent_type: 'data_usage' | 'notifications' | 'privacy_policy' | 'terms_of_service';
  granted: boolean;
  granted_at: string;
  version: string;
  ip_address?: string;
  user_agent?: string;
  created_at: string;
  updated_at: string;
}

export interface ConsentRequest {
  consent_type: UserConsent['consent_type'];
  granted: boolean;
  version: string;
  ip_address?: string;
  user_agent?: string;
}

export class ConsentService {
  /**
   * Record user consent for LGPD compliance
   */
  static async recordConsent(userId: string, consents: ConsentRequest[]): Promise<{ error: any }> {
    try {
      const consentRecords = consents.map(consent => ({
        user_id: userId,
        ...consent,
        granted_at: consent.granted ? new Date().toISOString() : null,
      }));

      const { error } = await supabase
        .from('user_consents')
        .insert(consentRecords);

      if (error) {
        safeLog.error('Error recording consent:', error);
        return { error };
      }

      safeLog.debug('✅ User consents recorded successfully');
      return { error: null };
    } catch (error) {
      safeLog.error('Exception recording consent:', error);
      return { error };
    }
  }

  /**
   * Get user consents
   */
  static async getUserConsents(userId: string): Promise<{ data: UserConsent[] | null; error: any }> {
    try {
      const { data, error } = await supabase
        .from('user_consents')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false });

      if (error) {
        safeLog.error('Error fetching user consents:', error);
        return { data: null, error };
      }

      return { data, error: null };
    } catch (error) {
      safeLog.error('Exception fetching user consents:', error);
      return { data: null, error };
    }
  }

  /**
   * Update user consent
   */
  static async updateConsent(
    userId: string, 
    consentType: UserConsent['consent_type'], 
    granted: boolean,
    version: string
  ): Promise<{ error: any }> {
    try {
      const { error } = await supabase
        .from('user_consents')
        .upsert({
          user_id: userId,
          consent_type: consentType,
          granted,
          granted_at: granted ? new Date().toISOString() : null,
          version,
          updated_at: new Date().toISOString(),
        }, {
          onConflict: 'user_id,consent_type'
        });

      if (error) {
        safeLog.error('Error updating consent:', error);
        return { error };
      }

      return { error: null };
    } catch (error) {
      safeLog.error('Exception updating consent:', error);
      return { error };
    }
  }

  /**
   * Check if user has granted specific consent
   */
  static async hasConsent(
    userId: string, 
    consentType: UserConsent['consent_type']
  ): Promise<{ granted: boolean; error: any }> {
    try {
      const { data, error } = await supabase
        .from('user_consents')
        .select('granted')
        .eq('user_id', userId)
        .eq('consent_type', consentType)
        .single();

      if (error && error.code !== 'PGRST116') { // PGRST116 = no rows found
        safeLog.error('Error checking consent:', error);
        return { granted: false, error };
      }

      return { granted: data?.granted || false, error: null };
    } catch (error) {
      safeLog.error('Exception checking consent:', error);
      return { granted: false, error };
    }
  }
}
